import { useReducedMotion } from "framer-motion";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { Step } from "react-joyride";
import { useWalkthroughGuideBeat } from "./useWalkthroughGuideBeat";
import { WalkthroughGuideSlot } from "./WalkthroughGuideSlot";
import { anchorSelector } from "./walkthroughAnchors";
import type { WalkthroughTooltipGuide } from "./walkthroughGuideContext";
import type { WalkthroughGuide } from "./walkthroughGuidePose";
import { walkthroughStepData } from "./walkthroughSteps";
import { watchWrongClicks } from "./walkthroughWrongClick";

interface Options {
	guide: WalkthroughGuide | undefined;
	isRunning: boolean;
	steps: Step[];
	stepIndex: number;
	onWrongClick: (stepIndex: number) => void;
}

interface TourGuide {
	tooltipGuide: WalkthroughTooltipGuide | null;
	onStepShown: () => void;
}

interface WrongClickOptions {
	enabled: boolean;
	targetSelector: string | null;
	revealAnchor: string | null;
	tooltip: HTMLElement | null;
	shownKey: number;
	onWrongClick: () => void;
}

/** Only opener steps ask for a click, so only there is a click elsewhere a wrong one. True until the next step opens. */
const useWrongClickHint = ({ enabled, targetSelector, revealAnchor, tooltip, shownKey, onWrongClick }: WrongClickOptions): boolean => {
	const [wrongClickAt, setWrongClickAt] = useState<number | null>(null);
	useEffect(() => {
		if (!enabled || targetSelector === null || revealAnchor === null) return;
		return watchWrongClicks(
			() => [document.querySelector(targetSelector), tooltip, document.querySelector(anchorSelector(revealAnchor))],
			() => {
				onWrongClick();
				setWrongClickAt(shownKey);
			},
		);
	}, [enabled, targetSelector, revealAnchor, tooltip, shownKey, onWrongClick]);
	return enabled && wrongClickAt === shownKey;
};

/** The tour card's guide slot and wrong-click hint; the hint shows with or without a guide, the figure only with one. */
export const useWalkthroughTourGuide = ({ guide, isRunning, steps, stepIndex, onWrongClick }: Options): TourGuide => {
	const reducedMotion = useReducedMotion() === true;
	const hasGuide = guide !== undefined && isRunning;
	const [shownKey, setShownKey] = useState(0);
	const [tooltip, setTooltip] = useState<HTMLElement | null>(null);
	useEffect(() => {
		if (!isRunning) setShownKey(0);
	}, [isRunning]);

	const step = steps[stepIndex];
	const targetSelector = typeof step?.target === "string" ? step.target : null;
	const { beat, oops } = useWalkthroughGuideBeat({
		enabled: hasGuide,
		shownKey,
		// DEFAULT: non-text copy (never authored today) talks for the minimum time.
		textLength: typeof step?.content === "string" ? step.content.length : 0,
		isLastStep: stepIndex === steps.length - 1,
		reducedMotion,
	});
	const handleWrongClick = useCallback(() => {
		oops();
		onWrongClick(stepIndex);
	}, [oops, onWrongClick, stepIndex]);
	const showWrongClickHint = useWrongClickHint({
		enabled: isRunning,
		targetSelector,
		revealAnchor: walkthroughStepData(step)?.revealAnchor ?? null,
		tooltip,
		shownKey,
		onWrongClick: handleWrongClick,
	});

	const onStepShown = useCallback(() => setShownKey((key) => key + 1), []);
	const tooltipGuide = useMemo<WalkthroughTooltipGuide | null>(
		() =>
			isRunning
				? {
						registerTooltip: setTooltip,
						slot:
							guide === undefined ? null : (
								<WalkthroughGuideSlot guide={guide} beat={beat} targetSelector={targetSelector} tooltip={tooltip} stepKey={stepIndex} />
							),
						showWrongClickHint,
					}
				: null,
		[guide, isRunning, beat, targetSelector, tooltip, stepIndex, showWrongClickHint],
	);

	return { tooltipGuide, onStepShown };
};
